import { computed, observable, action, toJS, runInAction, flow, makeObservable } from 'mobx'
import memoize from 'lodash/memoize'
import { types as t } from '@invisr/mobx-state-tree'
import errorToast from '../../../utils/error-toast'
import sortBy from 'lodash/sortBy'
import DataTransformer, { createOdataString } from '../filter'
import fetch from '../../../utils/fetch'
import delta from '../../../utils/delta'
import squash from '../../../utils/squash'
import upsertDataToArray from '../../../utils/upsertDataToArray'

import { getRecordClass } from '../record/record'
import epicorServices from './services.json'

export const callEpicor = async (actionName, method, config, body) => {
  if (!config) return

  const res = await fetch(`/api/datasource/${config.dbKey}/${actionName}`, {
    method: method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      credentials: encodeURIComponent(config.credentials)
    },
    body: JSON.stringify(body)
  })

  let result
  try {
    result = await res.json()
  } catch (err) {
    result = await res.text()
  }

  if (!res.ok) {
    let error
    try {
      error = JSON.parse(result.error)
    } catch (e) {
      error = result.error
    }
    throw error || result
  }

  return result
}

class EpicorObject {
  fields = []
  type
  config

  async fetchFields () {
    if (!this.fields.length && this.config.credentials && this.type) {
      await callEpicor('getObjectFields/' + this.type, 'GET', this.config)
        .then(
          action(result => {
            this.fields = result.map(f => {
              return {
                fieldPath: f.name,
                label: f.name,
                type: f.type,
                objectAPIName: this.type
              }
            })
          })
        )
        .catch(er => errorToast('Error getting Object fields: ', er))
    }
    return this.fields
  }

  constructor (config, businessObjectPath) {
    makeObservable(this, {
      fields: observable,
      fetchFields: action,
      fieldList: computed,
      fieldMap: computed
    })

    this.type = businessObjectPath || config.businessObjectPath
    this.config = config
    this.fetchFields()
  }

  get fieldList () {
    return this.fields
  }

  get fieldMap () {
    return this.fields && toJS(this.fields).reduce((acc, f) => { acc[f.fieldPath] = f; return acc }, {})
  }
}

export class EpicorMetadata {
  businessObject
  businessObjects = []

  constructor (credentials, config) {
    makeObservable(this, {
      businessObject: observable,
      businessObjects: observable
    })

    if (credentials) {
      this.credentials = credentials
      this.config = config
      this.businessObjects = epicorServices.map(o => ({ value: `${o.service}/${o.url}`, label: `${o.serviceFriendlyName}: ${o.name}` }))
    }

    this.businessObject = memoize((load = true) => {
      return new EpicorObject(config, load)
    })
  }
}

export class EpicorProvider {
  data = []
  config = null
  fieldSet = []
  isLoading = false
  isLookupLoading = false
  lookupValues = []

  setIsLoading = () => {
    this.isLoading = true
  }

  setIsLoadingOff = () => {
    this.isLoading = false
  }

  setIsLookupLoading = () => {
    this.isLookupLoading = true
  }

  setIsLookupLoadingOff = () => {
    this.isLookupLoading = false
  }

  constructor (config, fieldSet) {
    makeObservable(this, {
      data: observable.shallow,
      isLoading: observable,
      isLookupLoading: observable,
      lookupValues: observable,
      setIsLoading: action,
      setIsLoadingOff: action,
      setIsLookupLoading: action,
      setIsLookupLoadingOff: action,
      onValueChange: action
    })

    this.config = config
    this.fieldSet = fieldSet || []

    // for compatibility, may be unnecessary
    this.delete = this.remove
  }

  onValueChange = (fieldpath, v) => {
    this.data[0][fieldpath] = v
  }

  /*
  * not implemented
  */
  lookup = async (searchValue = '', referenceData = {}) => {
    if (searchValue.length > 2) {
      this.setIsLookupLoading()
      this.setIsLookupLoadingOff()
    }
  }

  /*
  * not implemented
  */
  getRecordByKey = (_, id, rd) => {
  }

  /*
  * not implemented
  */
  upsert = flow(function *test(obj, originalObj) {
    const s = epicorServices.find(o => o.name === this.config.businessObject && o.service === this.config.service)
    if (!s) throw Error('invalid epicor service')

    const recordsToUpdate = [...this.data]
    const rows = upsertDataToArray(obj, originalObj)

    for (let { item, original } of rows) {
      const originalClone = { ...original }
      item = delta(item, original)
      // put primary key to item
      original && s.keys.filter(k => original[k]).forEach(k => {
        item[k] = original[k]
      })
      const isUpdate = s.keys.every(k => item[k])

      // optimistic upsert
      let currentItemIndex = this.data.findIndex(x => s.keys.every(key => x[key] === item[key]))
      if (isUpdate && currentItemIndex >= 0 && this.data[currentItemIndex]) {
        const squashed = squash(item, original)
        this.data[currentItemIndex].$.update(squashed)
      } else {
        this.data.push(new this.config.Record(item))
        currentItemIndex = this.data.length - 1
      }

      delete item['odata.metadata']

      // real upsert/revert
      try {
        const result = yield callEpicor('upsert', 'POST', this.config, item)
        recordsToUpdate[currentItemIndex].$.update(result)
      } catch (err) {
        errorToast(err.ErrorMessage || JSON.stringify(err))
        if (isUpdate) recordsToUpdate[currentItemIndex].$.update(originalClone)
        else recordsToUpdate.splice(currentItemIndex, 1)
      }
    }
    this.data = recordsToUpdate
  }).bind(this)

  /*
  * not implemented
  */
  remove = async items => {
    const s = epicorServices.find(o => o.name === this.config.businessObject && o.service === this.config.service)
    if (!s) throw Error('invalid epicor service')

    this.setIsLoading()

    const rows = upsertDataToArray(items)
    for (const { item } of rows) {
      const idx = this.data.findIndex(x => s.keys.every(key => x[key] === item[key]))
      if (!idx) continue

      try {
        runInAction(() => this.data.splice(idx, 1))
        const keys = s.keys.map(key => item[key])
        await callEpicor('delete', 'POST', this.config, { keys })
      } catch (err) {
        runInAction(() => this.data.splice(idx, 0, item))
        errorToast(err.ErrorMessage || JSON.stringify(err))
        console.log(err)
        this.setIsLoadingOff()
        return
      }
    }

    this.setIsLoadingOff()
  }

  query = async context => {
    const odataFilter = createOdataString(this.config.dataTransformer || {})
    const result = await callEpicor(`query?${odataFilter}`, 'GET', this.config)

    runInAction(() => {
      this.data = (result.value && result.value.map(r => new this.config.Record(r))) || []
    })
  }
}

export const epicorDatasourceTypes = {
  OBJECT_DATA: {
    value: 'object_data',
    label: 'Query Epicor Object Data'
  }
}

const optionValues = Object.values(epicorDatasourceTypes).map(i => i.value)

let metadata = null
export const EpicorDataSource = t
  .model('Salesforce', {
    type: t.optional(t.string, 'Epicor'),
    option: t.optional(t.enumeration('option', optionValues), 'object_data'),
    businessObjectPath: t.maybeNull(t.string),
    dataTransformer: t.late(() => t.maybeNull(DataTransformer)),
    visibilityFilter: t.maybeNull(t.string)
  })
  .views(self => ({
    get businessObject () {
      return self.businessObjectPath && self.businessObjectPath.split('/')[1]
    },
    get service () {
      return self.businessObjectPath && self.businessObjectPath.split('/')[0]
    },
    get metadata () {
      if (!metadata || metadata.credentials !== self.credentials) metadata = new EpicorMetadata(self.credentials, self)
      return metadata
    },
    getFields (context) {
      return self.metadata
        .businessObject(context || self.businessObjectPath)
        .fieldList
    },
    get fieldSet () {
      return self.metadata.businessObject(self.businessObjectPath).fieldList
    },
    get provider () {
      return EpicorProvider
    },
    get fields () {
      return self.fieldSet
    }
  }))
  .views(self => ({
    get Record () {
      return getRecordClass({ recordType: self.id, recordFields: self.fieldSet })
    },
    async fetchChildNodes ({ meta } = {}) {
      return (

        self.metadata && self.metadata
          .businessObject(self.metadata.config.businessObjectPath)
          .fetchFields()
          .then(res => {
            const childNodes = sortBy(
              Object.keys(res).map(key => {
                const field = res[key]
                const node = {
                  label: field.label,
                  secondaryLabel: field.type,
                  meta: { ...toJS(field) }
                }
                return node
              }),
              node => node.label && node.label.toLowerCase()
            )

            return childNodes
          })
      )
    },
    fieldPath ({ meta = {} }) {
      return meta.fieldPath
    },
    labels () {
      const tempProvider = new EpicorProvider()
      const functions = Object.keys(tempProvider)
        .filter(k => typeof tempProvider[k] === 'function')
        .map(f => ({
          key: f,
          label: f,
          meta: { fieldPath: f },
          secondaryLabel: 'FUNCTION'
        }))
      functions.push({
        key: 'onValueChange',
        label: 'onValueChange',
        secondaryLabel: 'FUNCTION'
      })

      const labels = {
        key: self.id,
        label: self.name,
        secondaryLabel: self.businessObject,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: functions
          },
          {
            key: 'data',
            label: 'Data',
            meta: {
              businessObjectPath: self.businessObjectPath
            },
            asynch: true,
            childNodes: []
          },
          {
            key: 'state',
            label: 'State',
            childNodes: [
              {
                key: 'isLoading',
                label: 'isLoading',
                secondaryLabel: 'state'
              },
              {
                key: 'lookupValues',
                label: 'lookupValues',
                secondaryLabel: 'state'
              }
            ]
          }
        ]
      }
      return labels
    }
  }))
  .actions(self => ({
    update (key, val) {
      self[key] = val
    },
    updateQueryFields (fields) {
      self.queryFields = [...fields]
    },
    newTransformer () {
      self.dataTransformer = {}
    },
    updateItem (item) {
      // ditching multiple server-side filters (whereclauses) for now
      self.name = item.name || 'New DataSource'
      self.businessObjectPath = item.businessObjectPath
    },
    afterCreate () {
      if (!self.queryFields) self.queryFields = []
    }
  }))
export default EpicorDataSource
